import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Calendar, Users, Briefcase, ShieldCheck, Plus, MapPin, DollarSign,
  CheckCircle2, X, FileText, ArrowUpRight, AlertTriangle, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

/* ------------------------------------------------------------------ */
/*  Facility Operations — deep ops console for facility role          */
/*  Tabs: My Shifts · Job Applicants · Credentials Review              */
/* ------------------------------------------------------------------ */

const TABS = [
  { key: "shifts",      label: "My Shifts",          Icon: Calendar },
  { key: "applicants",  label: "Job Applicants",     Icon: Briefcase },
  { key: "credentials", label: "Credentials Review", Icon: ShieldCheck },
];

const STATUS_STYLES = {
  open:       "bg-amber-50 text-amber-700 border-amber-200",
  accepted:   "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled:  "bg-red-50 text-trital-red border-red-200",
  submitted:  "bg-amber-50 text-amber-700 border-amber-200",
  in_review:  "bg-trital-medical-blue-soft text-trital-navy border-trital-navy/20",
  interview:  "bg-blue-50 text-blue-700 border-blue-200",
  hired:      "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected:   "bg-red-50 text-trital-red border-red-200",
  pending:    "bg-amber-50 text-amber-700 border-amber-200",
  approved:   "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function FacilityOperations() {
  const [tab, setTab] = useState("shifts");
  const [shifts, setShifts] = useState([]);
  const [apps, setApps] = useState([]);
  const [creds, setCreds] = useState([]);
  const [credFilter, setCredFilter] = useState("pending");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, a, c] = await Promise.all([
        api.get("/shifts", { params: { mine: true } }),
        api.get("/applications/mine"),
        api.get("/credentials", { params: credFilter === "all" ? {} : { status: credFilter } }),
      ]);
      setShifts(s.data || []);
      setApps(a.data || []);
      setCreds(c.data || []);
    } catch (e) {
      toast.error("Couldn't load facility data", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [credFilter]);

  const kpis = useMemo(() => {
    const open      = shifts.filter((s) => s.status === "open").length;
    const accepted  = shifts.filter((s) => s.status === "accepted").length;
    const fillRate  = shifts.length ? Math.round((accepted / shifts.length) * 100) : 0;
    const pendApps  = apps.filter((a) => ["submitted", "in_review"].includes(a.status)).length;
    return [
      { Icon: Calendar,    label: "Open shifts",        value: open,         color: "navy" },
      { Icon: CheckCircle2,label: "Filled this week",   value: accepted,     color: "green" },
      { Icon: Briefcase,   label: "Applicants pending", value: pendApps,     color: "red" },
      { Icon: AlertTriangle,label: "Fill rate",         value: `${fillRate}%`, color: "blue" },
    ];
  }, [shifts, apps]);

  /* Actions */
  const cancelShift = async (id) => {
    if (!window.confirm("Cancel this shift? This is permanent.")) return;
    try {
      await api.post(`/shifts/${id}/action`, { action: "cancel" });
      toast.success("Shift cancelled");
      load();
    } catch (e) {
      toast.error("Couldn't cancel", { description: e?.response?.data?.detail || e.message });
    }
  };
  const setAppStatus = async (id, action) => {
    try {
      await api.post(`/applications/${id}/action`, { action });
      toast.success(`Application → ${action}`);
      load();
    } catch (e) {
      toast.error("Action failed", { description: e?.response?.data?.detail || e.message });
    }
  };
  const credAction = async (id, action) => {
    try {
      await api.post(`/credentials/${id}/action`, { action });
      toast.success(`Credential ${action}d`);
      load();
    } catch (e) {
      toast.error("Action failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="space-y-6" data-testid="facility-operations">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-trital-red">Facility operations</div>
          <h1 className="mt-1 font-display font-black text-3xl lg:text-4xl text-slate-900 tracking-tight">Workforce Console</h1>
          <p className="text-slate-600 text-sm mt-1">Track posted shifts, review applicants, approve clinician credentials.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/dashboard/post-shift" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-trital-navy text-white text-[13px] font-semibold btn-lift hover:bg-trital-navy-deep" data-testid="fac-post-shift">
            <Plus className="w-3.5 h-3.5" strokeWidth={2} /> Post shift
          </Link>
          <Link to="/dashboard/post-job" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-slate-300 text-slate-700 text-[13px] font-semibold hover:border-trital-navy hover:bg-slate-50" data-testid="fac-post-job">
            <Plus className="w-3.5 h-3.5" strokeWidth={2} /> Post job
          </Link>
        </div>
      </header>

      {/* KPI cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k, i) => <KpiCard key={k.label} {...k} index={i} />)}
      </div>

      {/* Tabs */}
      <nav className="flex gap-1 border-b border-slate-200" role="tablist" data-testid="facility-tabs">
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-3 -mb-px border-b-2 text-[13px] font-semibold transition-colors ${
                active ? "border-trital-red text-trital-navy" : "border-transparent text-slate-600 hover:text-trital-navy"
              }`}
              data-testid={`facility-tab-${t.key}`}
            >
              <t.Icon className="w-4 h-4" strokeWidth={1.8} /> {t.label}
            </button>
          );
        })}
      </nav>

      {loading ? (
        <div className="rounded-2xl bg-white border border-slate-200 p-10 text-center text-slate-500" data-testid="facility-loading">Loading…</div>
      ) : tab === "shifts" ? (
        <ShiftsTab shifts={shifts} onCancel={cancelShift} />
      ) : tab === "applicants" ? (
        <ApplicantsTab apps={apps} onAction={setAppStatus} />
      ) : (
        <CredentialsTab creds={creds} filter={credFilter} setFilter={setCredFilter} onAction={credAction} />
      )}
    </div>
  );
}

/* ============================================================== */
/*  Sub-components                                                 */
/* ============================================================== */

function KpiCard({ Icon, label, value, color, index }) {
  const palette = {
    navy:  "bg-trital-medical-blue-soft text-trital-navy",
    blue:  "bg-blue-50 text-blue-700",
    red:   "bg-red-50 text-trital-red",
    green: "bg-emerald-50 text-emerald-700",
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.04 }}
      className="rounded-2xl bg-white border border-slate-200 p-5 shadow-card"
      data-testid={`fac-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${palette[color]}`}><Icon className="w-5 h-5" strokeWidth={1.8} /></div>
      <div className="mt-3 font-display font-black text-3xl text-slate-900 tabular-nums">{value}</div>
      <div className="overline text-slate-500 mt-1">{label}</div>
    </motion.div>
  );
}

function ShiftsTab({ shifts, onCancel }) {
  if (shifts.length === 0) {
    return (
      <div className="rounded-2xl bg-slate-50 border border-dashed border-slate-300 p-10 text-center" data-testid="shifts-empty">
        <div className="font-display font-bold text-slate-900">No shifts posted yet.</div>
        <p className="mt-1.5 text-[13px] text-slate-600">Post your first shift to start matching with TRITAL Care clinicians.</p>
        <Link to="/dashboard/post-shift" className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-trital-navy text-white text-[13px] font-semibold btn-lift hover:bg-trital-navy-deep">
          <Plus className="w-3.5 h-3.5" strokeWidth={2} /> Post shift
        </Link>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="shifts-tab">
      {shifts.map((s) => (
        <motion.div
          key={s.id}
          initial={{ opacity: 0, y: 6 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.25 }}
          className="rounded-2xl bg-white border border-slate-200 p-5 shadow-card grid lg:grid-cols-[1.6fr_1fr_auto] gap-4 items-center"
          data-testid={`fac-shift-row-${s.id}`}
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider font-bold ${STATUS_STYLES[s.status] || "bg-slate-100 text-slate-700"}`}>{s.status}</span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-trital-medical-blue-soft text-trital-navy font-bold uppercase tracking-wider">{s.specialty}</span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-bold uppercase tracking-wider">{s.shift_type}</span>
            </div>
            <div className="mt-2 font-display font-bold text-slate-900 truncate">{s.title || `${s.specialty} · ${s.shift_type}`}</div>
            <div className="mt-1 text-[12px] text-slate-500 inline-flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" strokeWidth={1.8} /> {s.city}, {s.state}</span>
              <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" strokeWidth={1.8} /> {new Date(s.start_time).toLocaleString()}</span>
            </div>
            {s.accepted_by && (
              <div className="mt-1.5 text-[11.5px] text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" strokeWidth={2} /> Claimed</div>
            )}
          </div>
          <div className="flex flex-col">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-bold">Pay rate</div>
            <div className="font-display font-black text-trital-navy text-2xl tabular-nums leading-none mt-1"><DollarSign className="inline w-4 h-4 -mt-1 text-trital-red" strokeWidth={2.4} />{Number(s.hourly_rate || 0).toFixed(0)}<span className="text-slate-400 text-sm font-bold">/hr</span></div>
          </div>
          <div className="flex gap-2">
            {s.status !== "cancelled" && (
              <button
                type="button"
                onClick={() => onCancel(s.id)}
                className="px-3 py-1.5 rounded-full border border-red-200 text-trital-red text-[12px] font-semibold hover:bg-trital-red hover:text-white"
                data-testid={`fac-shift-cancel-${s.id}`}
              >
                Cancel
              </button>
            )}
          </div>
        </motion.div>
      ))}
    </div>
  );
}

function ApplicantsTab({ apps, onAction }) {
  if (apps.length === 0) {
    return (
      <div className="rounded-2xl bg-slate-50 border border-dashed border-slate-300 p-10 text-center" data-testid="applicants-empty">
        <div className="font-display font-bold text-slate-900">No applicants yet.</div>
        <p className="mt-1.5 text-[13px] text-slate-600">Once a clinician applies to one of your jobs, they'll show up here.</p>
        <Link to="/dashboard/post-job" className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-trital-navy text-white text-[13px] font-semibold btn-lift hover:bg-trital-navy-deep">
          <Plus className="w-3.5 h-3.5" strokeWidth={2} /> Post a job
        </Link>
      </div>
    );
  }
  return (
    <div className="rounded-2xl bg-white border border-slate-200 shadow-card overflow-hidden" data-testid="applicants-tab">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
          <tr>
            <th className="text-left px-5 py-3">Clinician</th>
            <th className="text-left px-5 py-3">Job</th>
            <th className="text-left px-5 py-3">Status</th>
            <th className="text-left px-5 py-3">Applied</th>
            <th className="text-right px-5 py-3">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {apps.map((a) => (
            <tr key={a.id} className="hover:bg-slate-50" data-testid={`fac-app-row-${a.id}`}>
              <td className="px-5 py-3 font-semibold text-slate-900">{a.clinician_name}</td>
              <td className="px-5 py-3 text-slate-600">{a.job_title}</td>
              <td className="px-5 py-3">
                <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider font-bold ${STATUS_STYLES[a.status] || "bg-slate-100 text-slate-700"}`}>{(a.status || "submitted").replace("_", " ")}</span>
              </td>
              <td className="px-5 py-3 text-[12px] text-slate-500">{a.created_at?.slice(0, 10)}</td>
              <td className="px-5 py-3 text-right">
                <div className="inline-flex gap-1.5 justify-end">
                  {a.status !== "interview" && a.status !== "hired" && a.status !== "rejected" && (
                    <button onClick={() => onAction(a.id, "interview")} className="px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-[11px] font-bold hover:bg-blue-700 hover:text-white" data-testid={`fac-app-interview-${a.id}`}>Interview</button>
                  )}
                  {a.status !== "hired" && a.status !== "rejected" && (
                    <button onClick={() => onAction(a.id, "hired")} className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-bold hover:bg-emerald-700 hover:text-white" data-testid={`fac-app-hire-${a.id}`}>Hire</button>
                  )}
                  {a.status !== "rejected" && a.status !== "hired" && (
                    <button onClick={() => onAction(a.id, "rejected")} className="px-2.5 py-1 rounded-full bg-red-50 text-trital-red text-[11px] font-bold hover:bg-trital-red hover:text-white" data-testid={`fac-app-reject-${a.id}`}>Reject</button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CredentialsTab({ creds, filter, setFilter, onAction }) {
  return (
    <div className="space-y-4" data-testid="credentials-tab">
      <div className="flex items-center gap-2 flex-wrap">
        {["pending", "approved", "rejected", "all"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-full text-[12px] font-semibold transition-colors ${
              filter === s ? "bg-trital-navy text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-trital-navy"
            }`}
            data-testid={`creds-filter-${s}`}
          >
            {s === "all" ? "All" : s}
          </button>
        ))}
        <span className="ml-auto text-[12px] text-slate-500">{creds.length} credentials</span>
      </div>

      {creds.length === 0 ? (
        <div className="rounded-2xl bg-slate-50 border border-dashed border-slate-300 p-10 text-center text-slate-500">No credentials in this view.</div>
      ) : (
        <div className="space-y-3">
          {creds.map((c) => (
            <div key={c.id} className="rounded-2xl bg-white border border-slate-200 p-5 shadow-card grid lg:grid-cols-[1.5fr_1fr_auto] gap-4 items-center" data-testid={`fac-cred-${c.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider font-bold ${STATUS_STYLES[c.status] || "bg-slate-100 text-slate-700"}`}>{c.status}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-trital-medical-blue-soft text-trital-navy font-bold uppercase tracking-wider">{c.type}</span>
                </div>
                <div className="mt-2 font-display font-bold text-slate-900 truncate">{c.user_name}</div>
                <div className="mt-1 text-[12.5px] text-slate-600 inline-flex items-center gap-3 flex-wrap">
                  {c.issuer && <span>{c.issuer}</span>}
                  {c.number && <span className="font-mono text-[11.5px] text-slate-500">#{c.number}</span>}
                  {c.expires_at && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" strokeWidth={1.8} /> Expires {c.expires_at?.slice(0, 10)}</span>}
                </div>
              </div>
              <div className="text-[12px] text-slate-500">
                {c.file_url ? (
                  <a href={c.file_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-trital-navy font-semibold hover:underline">
                    <FileText className="w-3.5 h-3.5" strokeWidth={1.8} /> View document <ArrowUpRight className="w-3 h-3" strokeWidth={1.8} />
                  </a>
                ) : <span className="text-slate-400">No file attached</span>}
              </div>
              {c.status === "pending" && (
                <div className="flex gap-1.5">
                  <button onClick={() => onAction(c.id, "approve")} className="px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-[12px] font-bold hover:bg-emerald-700 hover:text-white inline-flex items-center gap-1" data-testid={`fac-cred-approve-${c.id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} /> Approve
                  </button>
                  <button onClick={() => onAction(c.id, "reject")} className="px-3 py-1.5 rounded-full bg-red-50 text-trital-red text-[12px] font-bold hover:bg-trital-red hover:text-white inline-flex items-center gap-1" data-testid={`fac-cred-reject-${c.id}`}>
                    <X className="w-3.5 h-3.5" strokeWidth={2} /> Reject
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
